/**
 * Typed API client hook — wraps fetch with Clerk auth and shared error handling.
 *
 * All request functions are memoised with useCallback so useMutation/useQuery
 * callers receive stable references across renders.
 */

import { useCallback } from "react";

import { AUTH_DISABLED, useAppAuth } from "./auth";

// Proxied to http://localhost:8000 in dev (see vite.config.ts).
// In production the same origin serves both the SPA and the API.
const API_PREFIX = "/api/v1";

// ── Response types (mirror FastAPI response_model schemas) ────────────────────

export interface UploadResponse {
  file_id: string;
  run_id: string;
  status: string;
  message: string;
}

export interface FileStatusResponse {
  file_id: string;
  status: string; // QUEUED | PROCESSING | COMPLETE | FAILED
  filename: string;
  row_count: number | null;
  rows_written: number | null;
  rows_skipped: number | null;
  error_detail: string | null;
}

// ── Internal helpers ──────────────────────────────────────────────────────────

async function parseError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { detail?: string };
    return body.detail ?? res.statusText;
  } catch {
    return res.statusText;
  }
}

async function assertOk<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const detail = await parseError(res);
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useApiClient() {
  const { getToken } = useAppAuth();

  /** Build the Authorization header from the current Clerk session token. */
  const authHeader = useCallback(async (): Promise<HeadersInit> => {
    if (AUTH_DISABLED) return {};
    const token = await getToken();
    if (!token) throw new Error("Not authenticated.");
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  /**
   * POST /api/v1/files/manifest
   *
   * Epic 1 AC #8 — the manifest drop zone submits to this endpoint.
   */
  const uploadManifest = useCallback(
    async (file: File): Promise<UploadResponse> => {
      const headers = await authHeader();
      const body = new FormData();
      body.append("file", file);
      const res = await fetch(`${API_PREFIX}/files/manifest`, {
        method: "POST",
        headers, // no Content-Type — browser sets multipart boundary automatically
        body,
      });
      return assertOk<UploadResponse>(res);
    },
    [authHeader],
  );

  /**
   * POST /api/v1/files/invoice
   *
   * Epic 1 AC #8 — the carrier invoice drop zone submits to this endpoint.
   */
  const uploadInvoice = useCallback(
    async (file: File, carrierId: string): Promise<UploadResponse> => {
      const headers = await authHeader();
      const body = new FormData();
      body.append("file", file);
      body.append("carrier_id", carrierId);
      const res = await fetch(`${API_PREFIX}/files/invoice`, {
        method: "POST",
        headers,
        body,
      });
      return assertOk<UploadResponse>(res);
    },
    [authHeader],
  );

  /**
   * GET /api/v1/files/{fileId}/status
   *
   * Epic 1 AC #10 — polled by TanStack Query to track processing state.
   */
  const getFileStatus = useCallback(
    async (fileId: string): Promise<FileStatusResponse> => {
      const headers = await authHeader();
      const res = await fetch(`${API_PREFIX}/files/${fileId}/status`, { headers });
      return assertOk<FileStatusResponse>(res);
    },
    [authHeader],
  );

  return { uploadManifest, uploadInvoice, getFileStatus };
}
